/**
 * Manager for scene lifecycle and entity coordination
 */

import { Color3, Color4, Scene, Vector3 } from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import type { EventBus } from "../core/EventBus";
import type { AnyEngine } from "../engine/types";
import { CameraManager } from "./CameraManager";
import { GemManager } from "./GemManager";
import { LightManager } from "./LightManager";
import { MaterialManager } from "./MaterialManager";
import { MeshManager } from "./MeshManager";
import type { SceneManagerConfig, SceneOptions } from "./types";

/**
 * Manages scene creation, lifecycle, and sub-managers
 * Coordinates cameras, lights, meshes, and materials
 */
/**
 * Scene context for managing individual scene instances.
 * Sub-managers are optional for externally-created scenes.
 */
interface SceneContext {
  scene: Scene;
  cameraManager: CameraManager | null;
  lightManager: LightManager | null;
  meshManager: MeshManager | null;
  materialManager: MaterialManager | null;
  gemManager: GemManager | null;
}

export class SceneManager extends BaseManager {
  private scenes: Map<string, SceneContext> = new Map();
  private defaultSceneId: string | null = null;
  private config: SceneManagerConfig = {};

  /**
   * Create a new SceneManager
   * @param eventBus The event bus for manager communication
   * @param config Scene manager configuration
   */
  constructor(eventBus: EventBus, config?: SceneManagerConfig) {
    super(eventBus);
    this.config = config || {};
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    // Scene manager is ready - sub-managers are created per scene
    this.emit("sceneManagerReady", {
      config: this.config,
    });
  }

  /**
   * Generate a unique scene ID
   */
  private generateSceneId(): string {
    return `scene_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create and initialize sub-managers for a scene
   */
  private async createSubManagers(): Promise<Omit<SceneContext, "scene">> {
    const cameraManager = new CameraManager(this.eventBus);
    const lightManager = new LightManager(this.eventBus);
    const meshManager = new MeshManager(this.eventBus);
    const materialManager = new MaterialManager(this.eventBus);
    const gemManager = new GemManager(this.eventBus);

    // Initialize all sub-managers
    await Promise.all([
      cameraManager.initialize(),
      lightManager.initialize(),
      meshManager.initialize(),
      materialManager.initialize(),
      gemManager.initialize(),
    ]);

    return {
      cameraManager,
      gemManager,
      lightManager,
      materialManager,
      meshManager,
    };
  }

  /**
   * Create a new scene
   * @param engine The engine to create the scene with
   * @param sceneId Optional scene ID (generated if not provided)
   * @param options Scene creation options
   */
  async createScene(
    engine: AnyEngine,
    sceneId?: string,
    options?: SceneOptions,
  ): Promise<Scene> {
    this.ensureInitialized();

    // Generate or use provided scene ID
    const id = sceneId || this.generateSceneId();

    // Check if scene already exists - handle gracefully for SPA navigation
    const existing = this.scenes.get(id);
    if (existing) {
      if (existing.scene.isDisposed) {
        // Scene was disposed but not cleaned from map - remove stale entry
        this.scenes.delete(id);
        console.log(`[SceneManager] Cleaned up disposed scene: ${id}`);
      } else {
        // Scene is still active - reuse it (SPA-friendly)
        console.log(`[SceneManager] Reusing existing scene: ${id}`);
        return existing.scene;
      }
    }

    // Create sub-managers for this scene
    const managers = await this.createSubManagers();

    // Create the scene
    const scene = new Scene(engine);

    // Apply scene options
    const mergedOptions = {
      ...this.config.defaultSceneOptions,
      ...options,
    };

    this.applySceneOptions(scene, mergedOptions);

    // Set scene for sub-managers (guaranteed non-null from createSubManagers)
    managers.cameraManager?.setScene(scene);
    managers.lightManager?.setScene(scene);
    managers.meshManager?.setScene(scene);
    managers.materialManager?.setScene(scene);
    managers.gemManager?.setScene(scene);

    // Store the scene context
    const context: SceneContext = {
      scene,
      ...managers,
    };
    this.scenes.set(id, context);

    // Set as default if first scene
    if (this.scenes.size === 1) {
      this.defaultSceneId = id;
    }

    // Set up render callbacks
    scene.registerBeforeRender(() => {
      this.emit("beforeRender", { scene, sceneId: id });
    });

    scene.registerAfterRender(() => {
      this.emit("afterRender", { scene, sceneId: id });
    });

    // Enable debug layer if configured
    if (this.config.enableDebugLayer) {
      scene.debugLayer.show();
    }

    // Set up optimization if configured
    if (this.config.autoOptimize && this.config.targetFps) {
      this.setupOptimization(scene, this.config.targetFps);
    }

    // Emit scene created event
    this.emit("sceneCreated", { scene, sceneId: id });

    return scene;
  }

  /**
   * Apply options to a scene
   * @param scene The scene to configure
   * @param options The options to apply
   */
  private applySceneOptions(scene: Scene, options: SceneOptions): void {
    // Clear color
    if (options.clearColor) {
      if (options.clearColor instanceof Color4) {
        scene.clearColor = options.clearColor;
      } else {
        scene.clearColor = new Color4(
          options.clearColor.r,
          options.clearColor.g,
          options.clearColor.b,
          options.clearColor.a ?? 1,
        );
      }
    }

    // Ambient color
    if (options.ambientColor) {
      if (options.ambientColor instanceof Color3) {
        scene.ambientColor = options.ambientColor;
      } else {
        scene.ambientColor = new Color3(
          options.ambientColor.r,
          options.ambientColor.g,
          options.ambientColor.b,
        );
      }
    }

    // Gravity
    if (options.gravity) {
      if (options.gravity instanceof Vector3) {
        scene.gravity = options.gravity;
      } else {
        scene.gravity = new Vector3(
          options.gravity.x,
          options.gravity.y,
          options.gravity.z,
        );
      }
    }

    // Collisions
    if (options.collisionsEnabled !== undefined) {
      scene.collisionsEnabled = options.collisionsEnabled;
    }

    // Fog
    if (options.fogMode) {
      switch (options.fogMode) {
        case "exp":
          scene.fogMode = Scene.FOGMODE_EXP;
          break;
        case "exp2":
          scene.fogMode = Scene.FOGMODE_EXP2;
          break;
        case "linear":
          scene.fogMode = Scene.FOGMODE_LINEAR;
          break;
        default:
          scene.fogMode = Scene.FOGMODE_NONE;
      }
    }

    if (options.fogDensity !== undefined) {
      scene.fogDensity = options.fogDensity;
    }

    if (options.fogColor) {
      if (options.fogColor instanceof Color3) {
        scene.fogColor = options.fogColor;
      } else {
        scene.fogColor = new Color3(
          options.fogColor.r,
          options.fogColor.g,
          options.fogColor.b,
        );
      }
    }

    // Frustum clipping
    if (options.skipFrustumClipping !== undefined) {
      scene.skipFrustumClipping = options.skipFrustumClipping;
    }
  }

  /**
   * Set up scene optimization
   * @param scene The scene to optimize
   * @param targetFps Target frames per second
   */
  private setupOptimization(scene: Scene, targetFps: number): void {
    // Basic optimization based on FPS
    let lastFps = targetFps;
    scene.registerBeforeRender(() => {
      const engine = scene.getEngine();
      const currentFps = engine.getFps();

      // Simple optimization logic
      if (currentFps < targetFps * 0.9 && lastFps < targetFps * 0.9) {
        // Performance is low, reduce quality
        engine.setHardwareScalingLevel(2);
      } else if (currentFps > targetFps * 1.1 && lastFps > targetFps * 1.1) {
        // Performance is good, increase quality
        engine.setHardwareScalingLevel(1);
      }

      lastFps = currentFps;
    });
  }

  /**
   * Get a scene by ID, or the default scene if no ID provided
   */
  getScene(sceneId?: string): Scene | null {
    this.ensureInitialized();

    const id = sceneId || this.defaultSceneId;
    if (!id) return null;

    const context = this.scenes.get(id);
    return context?.scene || null;
  }

  /**
   * Get all scene IDs
   */
  getSceneIds(): string[] {
    return Array.from(this.scenes.keys());
  }

  /**
   * Get the default scene ID
   */
  getDefaultSceneId(): string | null {
    return this.defaultSceneId;
  }

  /**
   * Set the default scene ID
   */
  setDefaultSceneId(sceneId: string): void {
    if (!this.scenes.has(sceneId)) {
      throw new Error(`Scene with ID "${sceneId}" does not exist.`);
    }
    this.defaultSceneId = sceneId;
  }

  /**
   * Get the camera manager for a specific scene
   */
  getCameraManager(sceneId?: string): CameraManager | null {
    const id = sceneId || this.defaultSceneId;
    if (!id) return null;

    const context = this.scenes.get(id);
    return context?.cameraManager || null;
  }

  /**
   * Get the light manager for a specific scene
   */
  getLightManager(sceneId?: string): LightManager | null {
    const id = sceneId || this.defaultSceneId;
    if (!id) return null;

    const context = this.scenes.get(id);
    return context?.lightManager || null;
  }

  /**
   * Get the mesh manager for a specific scene
   */
  getMeshManager(sceneId?: string): MeshManager | null {
    const id = sceneId || this.defaultSceneId;
    if (!id) return null;

    const context = this.scenes.get(id);
    return context?.meshManager || null;
  }

  /**
   * Get the material manager for a specific scene
   */
  getMaterialManager(sceneId?: string): MaterialManager | null {
    const id = sceneId || this.defaultSceneId;
    if (!id) return null;

    const context = this.scenes.get(id);
    return context?.materialManager || null;
  }

  /**
   * Get the gem manager for a specific scene
   */
  getGemManager(sceneId?: string): GemManager | null {
    const id = sceneId || this.defaultSceneId;
    if (!id) return null;

    const context = this.scenes.get(id);
    return context?.gemManager || null;
  }

  /**
   * Dispose a specific scene or all scenes
   */
  disposeScene(sceneId?: string): void {
    if (sceneId) {
      // Dispose specific scene
      const context = this.scenes.get(sceneId);
      if (!context) {
        return;
      }

      // Clear sub-managers (null for externally-registered scenes)
      context.cameraManager?.clearAll();
      context.lightManager?.clearAll();
      context.meshManager?.clearAll();
      context.materialManager?.clearAll();
      context.gemManager?.clearAll();

      // Dispose sub-managers
      context.cameraManager?.dispose();
      context.lightManager?.dispose();
      context.meshManager?.dispose();
      context.materialManager?.dispose();
      context.gemManager?.dispose();

      // Dispose of the scene
      const sceneUid = context.scene.uid;
      context.scene.dispose();

      // Remove from map
      this.scenes.delete(sceneId);

      // Update default if needed
      if (this.defaultSceneId === sceneId) {
        const firstKey = this.scenes.keys().next();
        this.defaultSceneId = !firstKey.done ? firstKey.value : null;
      }

      // Emit scene disposed event
      this.emit("sceneDisposed", { sceneId, sceneUid });
    } else {
      // Dispose all scenes
      const sceneIds = Array.from(this.scenes.keys());
      for (const id of sceneIds) {
        this.disposeScene(id);
      }
    }
  }

  /**
   * Clear the current scene (backward compatibility)
   */
  clearScene(sceneId?: string): void {
    this.disposeScene(sceneId);
  }

  /**
   * Register an externally-created scene with the manager.
   * Useful for scenes created outside manager flow (e.g., physics demos).
   * Emits sceneCreated event for devtools integration.
   * @param sceneId Unique identifier for the scene
   * @param scene The Babylon.js scene to register
   */
  registerScene(sceneId: string, scene: Scene): void {
    if (this.scenes.has(sceneId)) {
      console.warn(`[SceneManager] Scene already registered: ${sceneId}`);
      return;
    }

    // Create minimal SceneContext (sub-managers null for external scenes)
    const context: SceneContext = {
      cameraManager: null,
      gemManager: null,
      lightManager: null,
      materialManager: null,
      meshManager: null,
      scene,
    };

    this.scenes.set(sceneId, context);

    // Set as default if first scene
    if (!this.defaultSceneId) {
      this.defaultSceneId = sceneId;
    }

    // Emit event for devtools
    this.emit("sceneCreated", { scene, sceneId });
  }

  /**
   * Unregister a scene without disposing it.
   * Use when external code manages scene lifecycle.
   * @param sceneId The scene ID to unregister
   */
  unregisterScene(sceneId: string): void {
    const context = this.scenes.get(sceneId);
    if (!context) {
      return;
    }

    this.scenes.delete(sceneId);

    // Update default if needed
    if (this.defaultSceneId === sceneId) {
      const firstKey = this.scenes.keys().next();
      this.defaultSceneId = !firstKey.done ? firstKey.value : null;
    }

    // Emit event for devtools
    this.emit("sceneDisposed", {
      sceneId,
      sceneUid: context.scene.uid.toString(),
    });
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    // Dispose all scenes
    this.disposeScene();

    // Clear the map
    this.scenes.clear();
    this.defaultSceneId = null;
  }
}
